import * as React from 'react';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceDot, ResponsiveContainer } from 'recharts';
import { exportVendorChart } from './NewVendorReportButtonHelpers2025';
import { numberWithCommas, billionNumberFormat, millionNumberFormat, makeNiceRechartsAxis } from '../../Common/Helpers';

export interface ImpactLineChartState {
    currentDateTime?: any;
    chartWidth?: number;
}

export interface ImpactLineChartProps {
    UserState?: any;
    establishmentsByYear?: any;
    spendingByYear?: any;
    chartLabel?: any;
    chartNum?: any;
    syncId?: any;
    selectedYear?: number;
}

export class ImpactLineChart extends React.Component<ImpactLineChartProps, ImpactLineChartState> {
    private chartContainerRef = React.createRef<HTMLDivElement>();
    
    constructor(props) {
        super(props);
        let now: Date = new Date();
        var currentDate = now.getFullYear() + "_" + (now.getMonth() + 1) + "_" + now.getDate();
        this.state = { currentDateTime: currentDate, chartWidth: 1000 };
    }

    componentDidMount() {
        this.updateChartWidth();
        window.addEventListener('resize', this.updateChartWidth);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateChartWidth);
    }

    updateChartWidth = () => {
        if (this.chartContainerRef.current) {
            this.setState({ chartWidth: this.chartContainerRef.current.clientWidth });
        }
    }

    render() {
        const spendAxis = makeNiceRechartsAxis(this.props.spendingByYear, 'spending', 5, 0.05);
        const estabAxis = makeNiceRechartsAxis(this.props.establishmentsByYear, 'establishmentCount', 5, 0.05);
        
        const selectedYear = this.props.selectedYear;
        const establishmentData = (this.props.establishmentsByYear ?? []) as Array<{ year: any; establishmentCount: any }>;
        const spendingData = (this.props.spendingByYear ?? []) as Array<{ year: any; spending: any }>;
        
        const chartWidth = this.state.chartWidth || 1000;
        const xAxisPadding = Math.round(chartWidth * 0.05);

        return (<div className="" ref={this.chartContainerRef} style={{ shapeRendering: 'auto', WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' }}>
            <div id={this.props.chartNum}>
                <div>
                    <h2 style={{ textAlign: "center", fontWeight: "bold" }}>{this.props.chartLabel} Impact</h2>
                    <h5 style={{ textAlign: "center" }} >Establishment Count and Spending Over Time</h5>
                    <ResponsiveContainer width="100%" height={300} style={{ filter: 'blur(0.3px)' }}>
                        <LineChart data={this.props.establishmentsByYear} margin={{ bottom: 20, left: 5, right: 5, top: 5 }} syncId={this.props.syncId}>
                            <CartesianGrid stroke="#e0e0e0" vertical={false} />
                            <YAxis domain={estabAxis.domain}
                                ticks={estabAxis.ticks}
                                tickFormatter={numberWithCommas}
                                label={{ value: this.props.chartLabel + " Count", angle: -90, fontSize: 16, position: "insideLeft" }} />
                            <XAxis dataKey="year"
                                tick={{ fontSize: 12 }}
                                label={{ value: "Year", position: "insideBottom", fontSize: 16, offset: -10 }}
                                padding={{ left: xAxisPadding, right: xAxisPadding }}
                            />
                            <Line 
                                name="Establishment Count" 
                                dataKey="establishmentCount" 
                                stroke="#4682b4"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                dot={false}
                                activeDot={{ r: 6 }}
                            />
                            {establishmentData.map((d) => (
                                <ReferenceDot
                                    key={`estab-dot-${d.year}`}
                                    x={d.year}
                                    y={d.establishmentCount}
                                    r={4}
                                    fill={selectedYear != null && d.year === selectedYear ? "#4682b4" : "#fff"}
                                    stroke="#4682b4"
                                    strokeWidth={1}
                                />
                            ))}
                            <Tooltip labelStyle={{ padding: 0 }} itemStyle={{ color: "#000" }} formatter={(value) => numberWithCommas(value)} cursor={false} />
                            {/*   <ReferenceDot x={selectedYear} y={vendorProfilesByYear.filter(d => d.start_year === Number(selectedYear))[0].total_pmt} r={5} fill="#4682b4" />
                            <ReferenceDot x={selectedYear} y={vendorProfilesByYear.filter(d => d.start_year === Number(selectedYear))[0].small_business_pmt_amt} r={5} fill="#ff8882" />
                                    <XAxis dataKey="year" hide={true} />
                            <Line dot={false} name="Small Business" type="monotone" dataKey="small_business_pmt_amt" stroke="#ff8882" />
                            <YAxis tickFormatter={(value) => numberWithCommas(value)} interval={1} />
                            */}
                        </LineChart>
                    </ResponsiveContainer>
                    <ResponsiveContainer width="100%" height={300} style={{ filter: 'blur(0.3px)' }}>
                        <LineChart data={this.props.spendingByYear} margin={{ bottom: 20, left: 5, right: 5, top: 5 }} syncId={this.props.syncId}>
                            <CartesianGrid stroke="#e0e0e0" vertical={false} />
                            <YAxis domain={spendAxis.domain}
                                ticks={spendAxis.ticks}
                                allowDecimals={false}
                                tickFormatter={millionNumberFormat}
                                label={{ value: this.props.chartLabel + " Spending ($M)", angle: -90, fontSize: 16, position: "insideLeft" }} />
                            <XAxis dataKey="year"
                                tick={{ fontSize: 12 }}
                                label={{ value: "Year", position: "insideBottom", fontSize: 16, offset: -10 }}
                                padding={{ left: xAxisPadding, right: xAxisPadding }}
                            />
                            <Line 
                                name="Spending" 
                                dataKey="spending" 
                                stroke="#ff8983"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                dot={false}
                                activeDot={{ r: 6 }}
                            />
                            {spendingData.map((d) => (
                                <ReferenceDot
                                    key={`spend-dot-${d.year}`}
                                    x={d.year}
                                    y={d.spending}
                                    r={4}
                                    fill={selectedYear != null && d.year === selectedYear ? "#ff8983" : "#fff"}
                                    stroke="#ff8983"
                                    strokeWidth={1}
                                />
                            ))}
                            <Tooltip labelStyle={{ padding: 0 }} itemStyle={{ color: "#000" }} formatter={(value) => billionNumberFormat(value)} cursor={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>)
    }
}